import {Alert, Card} from 'platform/components';
import {HStack, Link, Show, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {isNotNil} from 'ramda';

import i18n from '@dms/i18n';

import {useQueryState} from 'shared/web';

import {ErrorCategory} from '../types/ErrorCategory';
import {getErrorCategory} from '../utils/getErrorCategory';
import {parseErrorDescription} from '../utils/parseErrorDescription';

const getCategoryMessage = (category: ErrorCategory) =>
  match(category)
    .with('consent', () => i18n.t('page.settings.labels.microsoftOAuthError.consent'))
    .with('tenant', () => i18n.t('page.settings.labels.microsoftOAuthError.tenant'))
    .with('account', () => i18n.t('page.settings.labels.microsoftOAuthError.account'))
    .with('configuration', () => i18n.t('page.settings.labels.microsoftOAuthError.configuration'))
    .with('unknown', () => i18n.t('page.settings.labels.microsoftOAuthError.unknown'))
    .exhaustive();

export function MicrosoftErrorCode() {
  const [error] = useQueryState('error');
  const [errorDescription] = useQueryState('error_description');

  const parsed = errorDescription ? parseErrorDescription(errorDescription) : null;
  const errorUrl = `https://login.microsoftonline.com/error?code=${parsed?.code}`;
  const category = parsed ? getErrorCategory(parsed.code) : 'unknown';
  const decodedDescription = errorDescription
    ? decodeURIComponent(errorDescription.replace(/\+/g, ' '))
    : null;

  return (
    <VStack spacing={4}>
      <Alert
        variant="warning"
        title={i18n.t('page.settings.labels.microsoftOAuthError.title')}
        message={getCategoryMessage(category)}
      />

      <Card variant="inlineGrey">
        <VStack spacing={2}>
          <Show when={isNotNil(parsed)}>
            <HStack spacing={2}>
              <Text size="small" color="secondary">
                {i18n.t('page.settings.labels.microsoftOAuthError.errorCode')}
              </Text>
              <Text size="small" alternative>
                {`AADSTS${parsed?.code}`}
              </Text>
            </HStack>
            <HStack spacing={2}>
              <Text size="small" color="secondary">
                {i18n.t('page.settings.labels.microsoftOAuthError.details')}
              </Text>
              <Text size="small">{parsed?.message}</Text>
            </HStack>
            <HStack spacing={2}>
              <Text size="small" color="secondary">
                {i18n.t('page.settings.labels.microsoftOAuthError.link')}
              </Text>
              <Link href={errorUrl} target="_blank" size="small">
                {errorUrl}
              </Link>
            </HStack>
          </Show>

          <Show when={!parsed}>
            <Show when={isNotNil(error)}>
              <HStack spacing={2}>
                <Text size="small" color="secondary">
                  {i18n.t('page.settings.labels.microsoftOAuthError.errorCode')}
                </Text>
                <Text size="small" alternative>
                  {error}
                </Text>
              </HStack>
            </Show>
            <Show when={isNotNil(decodedDescription)}>
              <HStack spacing={2}>
                <Text size="small" color="secondary">
                  {i18n.t('page.settings.labels.microsoftOAuthError.details')}
                </Text>
                <Text size="small">{decodedDescription}</Text>
              </HStack>
            </Show>
          </Show>
        </VStack>
      </Card>
    </VStack>
  );
}
